import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Auth0Error } from 'auth0-js';
import { ZXCVBNFeedback } from 'zxcvbn';

import { Auth0SignupErrorCodes } from '@arrived/models';

/**
 * Options for the usePasswordHelperText hook.
 */
export interface UsePasswordHelperTextOptions {
  /**
   * The Auth0Error returned from the signup attempt.
   */
  auth0Error?: Auth0Error;
  /**
   * Boolean indicating if the user has met our password requirements.
   */
  hasMetPasswordRequirements: boolean;
  /**
   * Number 0-6 evaluating the password's strength (see usePasswordStrength.ts)
   */
  passwordStrength: number;
  /**
   * Feedback from evaluating the password with zxcvbn.
   */
  zxcvbnFeedback?: ZXCVBNFeedback;
}

/**
 * Returns the helper text to display for the password. We first display our error message in the case that the
 * password does not meet our requirements. Then precedence is given to the error returned from the signup request to
 * auth0 (if one exists). Next if there is feedback from zxcvbn we show that. Finally we return basic feedback to the
 * user evaluating the strength of their password based on the passwordStrength score.
 */
export function usePasswordHelperText({
  auth0Error,
  hasMetPasswordRequirements,
  passwordStrength,
  zxcvbnFeedback,
}: UsePasswordHelperTextOptions) {
  return useMemo(() => {
    if (!hasMetPasswordRequirements) {
      return <FormattedMessage id="signup.schema.password.requirements" />;
    }

    switch (auth0Error?.code) {
      case Auth0SignupErrorCodes.INVALID_PASSWORD:
      case Auth0SignupErrorCodes.PASSWORD_DICTIONARY_ERROR:
      case Auth0SignupErrorCodes.PASSWORD_STRENGTH_ERROR:
        return <FormattedMessage id="signup.schema.password.requirements" />;
      case Auth0SignupErrorCodes.PASSWORD_NO_USER_INFO_ERROR:
        return <FormattedMessage id="signup.schema.password.userInfo" />;
    }

    if (zxcvbnFeedback?.warning) {
      return zxcvbnFeedback?.warning;
    }

    if (passwordStrength >= 4) {
      return <FormattedMessage id="signup.schema.passwordStrength.strong" />;
    } else if (passwordStrength >= 2) {
      return <FormattedMessage id="signup.schema.passwordStrength.good" />;
    } else {
      // This case shouldn't happen very often if there's no feedback from zxcvbn
      return <FormattedMessage id="signup.schema.passwordStrength.weak" />;
    }
  }, [auth0Error, hasMetPasswordRequirements, passwordStrength, zxcvbnFeedback]);
}
